import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppSettings } from '../config/app-settings.js';
import coalService from '../services/CoalService/coal-service.js';
import relatoriosRTF from '../pages/carvao/relatorios-rtf-carvao.js';

/**
* Centraliza as ações sobre um registro de carvão (publicar, privar, editar,
* excluir, histórico e relatório individual) e o estado dos modais correspondentes.
* @param {object|null} selectedCoal   Registro selecionado; as ações dependem dele estar preenchido.
* @param {Function} setSelectedCoal   Setter usado para refletir a mudança de status localmente.
* @param {Array|null} fieldList       Estrutura de campos usada na geração do relatório RTF.
* @param {Function} [onStatusChange]  Chamado com (id, novoStatus) após publicar/privar, para a tela sincronizar a própria lista.
* @param {Function} [afterDelete]     Chamado após a exclusão ser confirmada pelo backend,
*                                     para a tela remover o registro da própria lista/estado.
*/

export default function useCoalActions(selectedCoal, setSelectedCoal, fieldList, onStatusChange, afterDelete) {
    const { t, i18n } = useTranslation();
    const { ...settings } = useContext(AppSettings);

    const [ printCoalList, setPrintCoalList ] = useState([]);
    const [ coalReportModal, setCoalReportModal ] = useState(false);
    const [ coalDeleteModal, setCoalDeleteModal ] = useState(false);
    const [ historyModal, setHistoryModal ] = useState(false);
    const [ historyList, setHistoryList ] = useState(null);

    const toggleModal = (name) => {
        if(name === 'coalReport') setCoalReportModal(!coalReportModal);
        else if(name === 'delete') setCoalDeleteModal(!coalDeleteModal);
    }

    const closeHistoryModal = () => setHistoryModal(false);

    const openReportModal = () => toggleModal("coalReport");
    const openDeleteModal = () => toggleModal("delete");

    const handleReportType = (type) => {
        if(type === 'coal-pdf'){
            setPrintCoalList([selectedCoal]);
            toggleModal("coalReport");
            setTimeout(function(){
                window.print();
                settings.handleSetAlert('success', t('alerta.titulo.sucesso'), t('relatório.alerta.relatórioFeito'));
            }, 200);
        }
        else if(type === 'coal-rtf'){
            toggleModal("coalReport");
            relatoriosRTF.getCoalReport([selectedCoal], fieldList, t, i18n);
            settings.handleSetAlert('success', t('alerta.titulo.sucesso'), t('relatório.alerta.relatórioFeito'));
        }
    }

    const coalHistory = async () => {
        const response = await coalService.coalHistory(selectedCoal.id);
        setHistoryList(response.data);
        setHistoryModal(true);
    }

    const publishCoal = async () => {
        const response = await coalService.publishCoal(selectedCoal.id);
        if (response.data == 'Carvão publicado') {
            setSelectedCoal({...selectedCoal, status: 2});
            settings.handleSetAlert('success', t('alerta.titulo.sucesso'), t('carvão.result.botão.alerta.registroPublicado'));
            onStatusChange?.(selectedCoal.id, 2);
        }
    }

    const unpublishCoal = async () => {
        const response = await coalService.unpublishCoal(selectedCoal.id);
        if (response.data == 'Carvão privado') {
            setSelectedCoal({...selectedCoal, status: 1});
            settings.handleSetAlert('success', t('alerta.titulo.sucesso'), t('carvão.result.botão.alerta.registroPrivado'));
            onStatusChange?.(selectedCoal.id, 1);
        }
    }

    const editCoal = () => {
        window.open('/editar-registro/' + selectedCoal.id, '_blank');
    }

    const viewCoal = () => {
        window.open('/visualiza-registro/' + selectedCoal.id, '_blank');
    }

    const deleteCoal = async () => {
        const response = await coalService.deleteCoal(selectedCoal.id);
        if (response.data == 'Carvão deletado') {
            settings.handleSetAlert('success', t('alerta.titulo.sucesso'), t('carvão.result.botão.alerta.registroDeletado'));
            afterDelete?.();
        }
    }

    return { printCoalList, coalReportModal, coalDeleteModal, historyModal, historyList,
        closeHistoryModal, openReportModal, openDeleteModal,
        handleReportType, coalHistory, publishCoal, unpublishCoal,
        editCoal, viewCoal, deleteCoal
    };
}